import { Transition } from "@headlessui/react"
import { Head, Link, router } from "@inertiajs/react"
import { ArrowLeft, Users } from "lucide-react"
import { useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import HeadingSmall from "@/components/heading-small"
import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface OrgData {
  id: number
  name: string
  slug: string
  seat_limit: number
  members_count: number
  seats_available: number
  logo_url?: string | null
}

interface Membership {
  role: string
}

interface Props {
  organization: OrgData
  membership: Membership
  errors?: Record<string, string>
}

export default function OrganizationSettings({
  organization,
  errors = {},
}: Props) {
  const { t } = useTranslation()
  const [name, setName] = useState(organization.name)
  const [slug, setSlug] = useState(organization.slug)
  const [logoPreview, setLogoPreview] = useState<string | null>(
    organization.logo_url ?? null,
  )
  const [processing, setProcessing] = useState(false)
  const [recentlySuccessful, setRecentlySuccessful] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  const breadcrumbs: BreadcrumbItem[] = [
    {
      title: t("organization_settings.breadcrumb_all"),
      href: "/organizations",
    },
    { title: t("organization_settings.breadcrumb_settings"), href: "#" },
  ]

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setLogoPreview(URL.createObjectURL(file))
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setProcessing(true)

    const formData = new FormData()
    formData.append("organization[name]", name)
    formData.append("organization[slug]", slug)
    if (fileRef.current?.files?.[0]) {
      formData.append("organization[logo]", fileRef.current.files[0])
    }

    router.patch(`/organizations/${organization.id}`, formData, {
      forceFormData: true,
      onSuccess: () => {
        setRecentlySuccessful(true)
        setTimeout(() => setRecentlySuccessful(false), 2000)
      },
      onFinish: () => setProcessing(false),
    })
  }

  const getInitial = (n: string) => n.charAt(0).toUpperCase()

  const handleDelete = () => {
    router.delete(`/organizations/${organization.id}`)
    setDeleteModalOpen(false)
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={t("organization_settings.title")} />

      <div className="flex h-full flex-1 flex-col gap-6 p-6">
        <div className="space-y-6">
          <div>
            <Link
              href="/organizations"
              className="text-muted-foreground hover:text-foreground mb-4 inline-flex items-center gap-1 text-sm"
            >
              <ArrowLeft className="size-4" />
              {t("organization_settings.back_to_all")}
            </Link>
            <HeadingSmall
              title={organization.name}
              description={t("organization_settings.description")}
            />
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Logo */}
            <div className="space-y-2">
              <Label>{t("organization_settings.logo")}</Label>
              <div className="flex items-center gap-4">
                <div className="border-border bg-primary/10 flex h-16 w-16 items-center justify-center overflow-hidden rounded-full border-2">
                  {logoPreview ? (
                    <img
                      src={logoPreview}
                      alt="Logo"
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <span className="text-primary text-2xl font-bold">
                      {getInitial(name || "O")}
                    </span>
                  )}
                </div>
                <div className="space-y-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileRef.current?.click()}
                  >
                    {t("organization_settings.change_logo")}
                  </Button>
                  <p className="text-muted-foreground text-xs">
                    {t("organization_settings.logo_hint")}
                  </p>
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleLogoChange}
                />
              </div>
              {errors.logo && <InputError message={errors.logo} />}
            </div>

            {/* Name */}
            <div className="grid gap-2">
              <Label htmlFor="org-name">
                {t("organization_settings.name")}
              </Label>
              <Input
                id="org-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="max-w-sm"
              />
              {errors.name && <InputError message={errors.name} />}
            </div>

            {/* Slug */}
            <div className="grid gap-2">
              <Label htmlFor="org-slug">
                {t("organization_settings.slug")}
              </Label>
              <Input
                id="org-slug"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                required
                pattern="[a-z0-9-]+"
                className="max-w-sm font-mono text-sm"
              />
              <p className="text-muted-foreground text-xs">
                {t("organization_settings.slug_hint")}
              </p>
              {errors.slug && <InputError message={errors.slug} />}
            </div>

            <div className="flex items-center gap-4">
              <Button disabled={processing}>
                {t("organization_settings.save")}
              </Button>
              <Transition
                show={recentlySuccessful}
                enter="transition ease-in-out"
                enterFrom="opacity-0"
                leave="transition ease-in-out"
                leaveTo="opacity-0"
              >
                <p className="text-sm text-neutral-600">
                  {t("organization_settings.saved")}
                </p>
              </Transition>
            </div>
          </form>

          {/* Read-only info */}
          <div className="space-y-4 border-t pt-6">
            <HeadingSmall
              title={t("organization_settings.plan_info.title")}
              description={t("organization_settings.plan_info.description")}
            />
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div className="rounded-lg border p-4 text-center">
                <p className="text-primary text-2xl font-bold">
                  {organization.members_count}
                </p>
                <p className="text-muted-foreground">
                  {t("organization_settings.plan_info.members")}
                </p>
              </div>
              <div className="rounded-lg border p-4 text-center">
                <p className="text-primary text-2xl font-bold">
                  {organization.seats_available}
                </p>
                <p className="text-muted-foreground">
                  {t("organization_settings.plan_info.seats_available")}
                </p>
              </div>
              <div className="rounded-lg border p-4 text-center">
                <p className="text-primary text-2xl font-bold">
                  {organization.seat_limit}
                </p>
                <p className="text-muted-foreground">
                  {t("organization_settings.plan_info.seat_limit")}
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm" asChild>
              <a href="/cuppers">
                <Users className="mr-2 h-4 w-4" />
                {t("organization_settings.plan_info.manage_cuppers")}
              </a>
            </Button>
          </div>

          {/* Delete Organization */}
          <div className="border-destructive/20 space-y-4 border-t pt-6">
            <HeadingSmall
              title={t(
                "organization_settings.delete.title",
                "Delete Organization",
              )}
              description={t(
                "organization_settings.delete.description",
                "Permanently delete this organization and all its data.",
              )}
            />
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setDeleteModalOpen(true)}
            >
              {t("organization_settings.delete.button", "Delete Organization")}
            </Button>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t(
                "organization_settings.delete.modal_title",
                "Delete Organization",
              )}
            </DialogTitle>
            <DialogDescription>
              {t(
                "organization_settings.delete.modal_description",
                "Are you sure you want to delete",
              )}{" "}
              <strong>"{organization.name}"</strong>?{" "}
              {t(
                "organization_settings.delete.modal_warning",
                "This action cannot be undone and will delete all samples, cuppings, and data associated with this organization.",
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
              {t("organization_settings.delete.cancel", "Cancel")}
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              {t(
                "organization_settings.delete.confirm_button",
                "Delete Organization",
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  )
}
